import React from "react";
import { Link } from "react-router-dom";
import { Archive, ArchiveRestore, ExternalLink, HandHeart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { profileHref } from "@/components/social/AuthorLink";
import { OtterAssistHelpButton } from "@/components/social/OtterAssistHelpButton";
import { HelperNoteEditor } from "@/components/social/HelperNote";
import { needHref, postHref } from "@/lib/otterAssistRoutes";

// Otter Assist — one Assistance Offered card (OAD-107 … OAD-110).
//
// The object is the NEED. Withdraw / Offer to Help Again reuse the ONE canonical help control, so
// eligibility, cooldown, reversal limits and lifecycle rules stay with the backend. Nothing here
// shows a Match Quality score, band, rank or any sender-private status.

const URGENCY_COPY = {
  immediate: "Immediate — today", asap: "ASAP — within 48 hours", soon: "Soon — within 7 days",
  within_days: "Within a set number of days", flexible: "Flexible — no firm deadline",
};
const STATUS_COPY = {
  active: "Open for help", help_received: "Help received", closed: "Closed", expired: "Expired",
  cancelled: "Cancelled",
};
const SOURCE_COPY = {
  assist_delivery: "Matched by Otter Assist", public_discovery: "You found this post",
};

export function AssistanceOfferedCard({ item, view, onArchive }) {
  const poster = item.poster || {};
  const href = profileHref(poster);
  const terminal = item.assist_status !== "active";
  const offerLabel = item.offer_state === "offered" ? "You offered to help" : "Offer withdrawn";

  return (
    <article className="rounded-xl border border-border bg-card p-4"
      data-testid={`assistance-offered-card-${item.action_id}`}>
      <div className="flex items-start gap-3">
        {poster.headshot_url ? (
          <img src={poster.headshot_url} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" />
        ) : (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary text-[13px] font-[640] text-muted-foreground">
            {(poster.display_name || "M").slice(0, 1)}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {href ? (
              <Link to={href} className="truncate text-sm font-[640] text-foreground hover:underline"
                data-testid={`assistance-offered-poster-${item.action_id}`}>
                {poster.display_name}
              </Link>
            ) : (
              <span className="truncate text-sm font-[640] text-foreground">
                {poster.display_name}
              </span>
            )}
            <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-[600] uppercase tracking-wide text-muted-foreground">
              {poster.profile_type || "personal"}
            </span>
            <span className="rounded-full bg-teal-700 px-2 py-0.5 text-[9px] font-[700] uppercase tracking-[0.08em] text-white">
              Otter Assist
            </span>
            {item.is_updated && (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-[600] text-amber-900"
                data-testid={`assistance-offered-updated-${item.action_id}`}>Updated</span>
            )}
          </div>

          <div className="mt-1 text-[11px] text-muted-foreground">
            <span data-testid={`assistance-offered-state-${item.action_id}`}>{offerLabel}</span>
            {item.offered_at ? ` · ${new Date(item.offered_at).toLocaleString()}` : ""}
            {" · "}
            <span data-testid={`assistance-offered-status-${item.action_id}`}>
              {STATUS_COPY[item.assist_status] || item.assist_status}
            </span>
            {item.source ? ` · ${SOURCE_COPY[item.source] || ""}` : ""}
          </div>

          <p className="mt-2 text-[13px] text-foreground/90">{item.summary}</p>

          <div className="mt-1.5 text-[11px] text-muted-foreground">
            {URGENCY_COPY[item.urgency] || item.urgency}
            {item.deadline_at
              ? ` · Deadline ${new Date(item.deadline_at).toLocaleDateString()}`
              : ""}
          </div>

          {item.why_you_matched && (
            <p className="mt-2 rounded-md border border-yellow-300/70 bg-yellow-50 px-2 py-1.5 text-[11px] text-foreground/90"
              data-testid={`assistance-offered-why-${item.action_id}`}>
              {item.why_you_matched}
            </p>
          )}

          {/* OAD-111 — the helper's OWN optional message, editable here and on Need Detail. It is
              preserved after a withdrawal and clearly marked so, never silently deleted. */}
          <HelperNoteEditor actionId={item.action_id} note={item.helper_note}
            withdrawn={item.offer_state === "withdrawn"} testIdSuffix={item.action_id} />

          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {/* OAD-113 — View Need always opens the canonical Need Detail, including for a
                public-discovery offer that has no delivery at all. */}
            <Button asChild type="button" size="sm" variant="outline" className="h-7 text-[11px]">
              <Link to={needHref(item.assist_id, "assistance-offered")}
                data-testid={`assistance-offered-view-need-${item.action_id}`}>
                <HandHeart className="mr-1.5 h-3 w-3" /> View Need
              </Link>
            </Button>
            {item.post_id && (
              <Button asChild type="button" size="sm" variant="outline" className="h-7 text-[11px]">
                <Link to={postHref(item.post_id, "assistance-offered")}
                  data-testid={`assistance-offered-view-post-${item.action_id}`}>
                  <ExternalLink className="mr-1.5 h-3 w-3" /> View Post
                </Link>
              </Button>
            )}
            {/* The ONE canonical help control: it decides Withdraw vs Offer to Help Again, and it
                honours cooldown, reversal limits and terminal lifecycles server-side. */}
            {!terminal && (
              <OtterAssistHelpButton assistId={item.assist_id}
                testIdSuffix={`assistance-${item.action_id}`} size="sm" />
            )}
            <Button type="button" size="sm" variant="ghost" className="h-7 text-[11px]"
              onClick={() => onArchive(view !== "archived")}
              data-testid={`assistance-offered-archive-${item.action_id}`}>
              {view === "archived"
                ? (<><ArchiveRestore className="mr-1.5 h-3 w-3" /> Restore</>)
                : (<><Archive className="mr-1.5 h-3 w-3" /> Archive</>)}
            </Button>
          </div>
        </div>
      </div>
    </article>
  );
}

export default AssistanceOfferedCard;
